/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  Tooltip,
  Typography,
  Table,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import API from "../api";
import urls from "../api/urls";
import {
  checkAlphabets,
  checkNumbervalue,
  checkSpace,
  checkSpecialCharacter,
} from "../utils";

const { GET_BANK_DETAILS_BY_IFSC } = urls;

const BankDetails = ({ disabled, form, bankDetails }) => {
  const { Text } = Typography;
  const [editingIndex, setEditingIndex] = useState(null);

  const handleEdit = (index) => {
    setEditingIndex(index);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const checkIFSC = async (values, fieldIndex) => {
    let response = await API.Api(
      GET_BANK_DETAILS_BY_IFSC + `?ifscCode=${values}`
    ).get();

    if (response?.status) {
      const details = response?.response?.data;

      // Update form values dynamically
      const bankDetails = form.getFieldValue("bankDetails") || [];

      bankDetails[fieldIndex] = {
        ...bankDetails[fieldIndex],
        bankName: details?.BANKCODE || "",
        branchName: details?.BRANCH || "",
        micrNumber: details?.MICR || "",
        swiftCode: details?.SWIFT || "",
      };

      form.setFieldsValue({ bankDetails });
    }
  };

  return (
    <>
      <Form.List name="bankDetails">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }, index) => (
              <Row
                key={key}
                gutter={12}
                style={{
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid #f0f0f0",
                  padding: "5px 0",
                }}
              >
                <Col span={4}>
                  <Form.Item
                    {...restField}
                    name={[name, "accountHolderName"]}
                    label="Account Holder Name"
                    rules={[
                      {
                        validator: async (_, value) => {
                          if (!value || value.trim() === "") {
                            throw new Error("Enter account holder name");
                          }
                        },
                      },
                    ]}
                    onKeyPress={(event) => {
                      if (
                        checkSpecialCharacter(event) ||
                        checkAlphabets(event)
                      ) {
                        event.preventDefault();
                      }
                    }}
                  >
                    <Input
                      placeholder="Account Holder Name"
                      disabled={disabled || editingIndex !== index}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    {...restField}
                    name={[name, "accountNumber"]}
                    label="Account Number"
                    rules={[
                      { required: true, message: "Enter account number" },
                      {
                        validator: async (_, value) => {
                          if (!value) return;
                          const bankDetails =
                            form.getFieldValue("bankDetails") || [];
                          const duplicateIndex = bankDetails.findIndex(
                            (detail, idx) =>
                              idx !== index && detail?.accountNumber === value
                          );
                          if (duplicateIndex !== -1) {
                            throw new Error("Account number already exists");
                          }
                        },
                      },
                    ]}
                    onKeyPress={(event) => {
                      if (
                        checkSpecialCharacter(event) ||
                        checkSpace(event) ||
                        checkNumbervalue(event)
                      ) {
                        event.preventDefault();
                      }
                    }}
                  >
                    <Input
                      placeholder="Account Number"
                      maxLength={18}
                      disabled={disabled || editingIndex !== index}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    {...restField}
                    name={[name, "ifscCode"]}
                    label="IFSC Code"
                    rules={[
                      { required: true, message: "Enter IFSC code" },
                      {
                        pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/g,
                        message: "Invalid IFSC code",
                      },
                    ]}
                  >
                    <Input
                      placeholder="IFSC Code"
                      maxLength={11}
                      onInput={(e) => {
                        e.target.value = e.target.value.toUpperCase();
                      }}
                      onChange={(event) => {
                        if (event.target.value.length > 10) {
                          checkIFSC(event.target.value, index);
                        }
                      }}
                      disabled={disabled || editingIndex !== index}
                    />
                  </Form.Item>
                </Col>

                <Col span={5} style={{ marginTop: "10px" }}>
                  <Text type="secondary" style={{ fontSize: "11px" }}>
                    Bank:{" "}
                    {form.getFieldValue(["bankDetails", name, "bankName"]) ||
                      "N/A"}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: "11px" }}>
                    Branch:{" "}
                    {form.getFieldValue(["bankDetails", name, "branchName"]) ||
                      "N/A"}
                    <Tooltip
                      className="pointer"
                      title={
                        <>
                          <p>
                            MICR:{" "}
                            {form.getFieldValue([
                              "bankDetails",
                              name,
                              "micrNumber",
                            ]) || "-"}
                          </p>
                          <p>
                            SWIFT:{" "}
                            {form.getFieldValue([
                              "bankDetails",
                              name,
                              "swiftCode",
                            ]) || "-"}
                          </p>
                        </>
                      }
                    >
                      <InfoCircleOutlined
                        style={{ marginLeft: 8, color: "#1890ff" }}
                      />
                    </Tooltip>
                  </Text>
                </Col>

                <Form.Item name={[name, "bankName"]} label="Bank Name" hidden>
                  <Input placeholder="Bank Name" disabled />
                </Form.Item>

                <Form.Item name={[name, "branchName"]} label="Branch" hidden>
                  <Input placeholder="Branch" disabled />
                </Form.Item>

                <Form.Item name={[name, "micrNumber"]} label="MICR" hidden>
                  <Input placeholder="MICR Code" disabled />
                </Form.Item>

                <Form.Item name={[name, "swiftCode"]} label="SWIFT" hidden>
                  <Input placeholder="SWIFT Code" disabled />
                </Form.Item>

                {/* Remove Button */}
                {!disabled && (
                  <Col span={1}>
                    {editingIndex === index ? (
                      <Button type="link" onClick={handleCancelEdit}>
                        Save
                      </Button>
                    ) : (
                      <Button type="link" onClick={() => handleEdit(index)}>
                        EDIT
                      </Button>
                    )}
                  </Col>
                )}
                {!disabled && (
                  <Col span={1}>
                    <Button type="link" onClick={() => remove(name)}>
                      <MinusCircleOutlined
                        style={{ fontSize: "18px", color: "red" }}
                      />
                    </Button>
                  </Col>
                )}
              </Row>
            ))}

            {!disabled && (
              <Button
                type="dashed"
                onClick={() => {
                  const newIndex = fields.length;
                  add();
                  setEditingIndex(newIndex);
                }}
                icon={<PlusOutlined />}
              >
                Add Bank Details
              </Button>
            )}
          </>
        )}
      </Form.List>
    </>
  );
};

export default BankDetails;
